import {
  FieldType,
  type GenericMap,
  type RangeSettingDefinitionOptions,
  ScreenType,
  type SettingDefinition,
  type SidesSettingDefinitionOptions,
  SidesType
} from "@client/types";
import { COMMON_DEFINITIONS_ALL } from "./lib";

import { joinDefined } from "@client/lib/misc";
import { get, isNull, isUndefined, set } from "lodash";

import { traverseObject } from "@client/lib/object";
import { isResponsiveValue } from "@client/lib/settings";

// NOTE: uppercase to easily distinguish
import BASE_STRUCTURE from "./structure.json";

export type SettingsStructure = GenericMap<any>;
export type SettingsStructureMetaSetting = {
  span?: number;
  help?: string;
  label?: string;
};
export type SettingsStructureMeta = {
  av?: string;
  def?: string;
  label?: string;
  prop?: string;
  val?: any;

  settings?: GenericMap<SettingsStructureMetaSetting>;
  Component?: React.ComponentType<any>;
  preview?: string;
};
export type SettingsStructureSetting = {
  av?: string;
  inheritFrom?: string;
  dependencies?: GenericMap<any>;
  label?: string;
  help?: string;
  def: string;
  val?: any;

  source?: string; // TODO: provide explanation
};

export const KNOWN_DEFINITIONS: GenericMap<SettingDefinition> = {
  range: {
    type: FieldType.Range,
    opts: {} as RangeSettingDefinitionOptions
  },
  margin: {
    type: FieldType.Sides,
    opts: {
      subType: SidesType.Margin
    } as SidesSettingDefinitionOptions
  },
  padding: {
    type: FieldType.Sides,
    opts: {
      subType: SidesType.Padding
    } as SidesSettingDefinitionOptions
  },
  width: {
    type: FieldType.Sides,
    opts: {
      subType: SidesType.Border
    } as SidesSettingDefinitionOptions
  },
  widthHeight: {
    type: FieldType.WidthHeight
  }
};

export const getDefinitionBaseInner = (
  commonDefinitionsAll: GenericMap<SettingDefinition>,
  key: string,
  definitions?: GenericMap<SettingDefinition> | null
): SettingDefinition | undefined =>
  (definitions ? definitions[key] : null) ?? commonDefinitionsAll[key] ?? KNOWN_DEFINITIONS[key];

export const getDefinitionBase = (
  key: string,
  definitions?: GenericMap<SettingDefinition> | null
): SettingDefinition | undefined => getDefinitionBaseInner(COMMON_DEFINITIONS_ALL, key, definitions);

export const getDefinitionInner = (
  commonDefinitionsAll: GenericMap<SettingDefinition>,
  key: string,
  s: SettingsStructureSetting,
  definitions?: GenericMap<SettingDefinition> | null
): SettingDefinition => {
  const def = s.def ?? key;
  const definition = getDefinitionBaseInner(commonDefinitionsAll, def, definitions);

  if (!definition) throw new Error(`BUG. Cannot find "${def}" definition. Setting: ${JSON.stringify(s)}`);

  return definition;
};
export const getDefinition = (
  key: string,
  s: SettingsStructureSetting,
  definitions?: GenericMap<SettingDefinition> | null
): SettingDefinition => getDefinitionInner(COMMON_DEFINITIONS_ALL, key, s, definitions);

export const getDefinitionDefaultValue = (value: any, screen: ScreenType) => {
  if (value && typeof value === "object") {
    switch (screen) {
      case ScreenType.Desktop:
        return value._desktop ?? value;
    }
  }

  return value;
};

type IterateStructureEnterArgs = {
  path: string;
  key: string;
  isSetting: boolean;
  structure: SettingsStructure;
  isOptional: boolean;

  prop?: string | null; // layout object prop name
};
type IterateStructureEnter = (args: IterateStructureEnterArgs) => boolean;

export const iterateStructureInner = (
  baseStructure: SettingsStructure,
  structure: SettingsStructure,
  enter: IterateStructureEnter,
  cl?: GenericMap<any>
) => {
  const getFromCommonStructure = (key: string) => (cl ? cl[key] : null) ?? baseStructure[key];

  const iterate = (l: GenericMap<any>, path: string, isOptional: boolean) => {
    const keys = Object.keys(l);
    for (const x of keys) {
      const type = x[0];
      switch (type) {
        case "[": // mandatory drill down
        case "+": // accordion with +/-
        case "(": // optional drill down
        case "{": // tab
        case ">": // open accordion
        case "<": // drill down w/o a property
        case "~": {
          // expand
          let childStructure =
            typeof l[x] === "string" ? getFromCommonStructure(l[x]) : isNull(l[x]) ? getFromCommonStructure(x) : l[x];
          if (!childStructure)
            throw new Error(
              `BUG. Cannot find structure for ${joinDefined(".", path, x)} in ${JSON.stringify(structure)}`
            );

          const meta: SettingsStructureMeta | undefined = childStructure["$"];

          const prop =
            meta?.prop ?? (x.startsWith("+") || x.startsWith("(") || x.startsWith("[") ? getPropName(x) : null);

          isOptional ||= type.startsWith("(") || type.startsWith("+");

          const r = enter({
            structure: childStructure,
            key: x,
            path,
            prop,
            isSetting: false,
            isOptional
          });
          if (r) {
            if (meta?.def) {
              childStructure = getFromCommonStructure(meta.def);
              if (!childStructure) {
                throw new Error(
                  `BUG. Cannot find structure for ${joinDefined(".", path, meta.def)} in ${JSON.stringify(structure)}`
                );
              }
            }

            iterate(childStructure, joinDefined(".", path, prop), isOptional);
          }

          break;
        }
        case "$": {
          // meta
          if (l[x].def) {
            const r = enter({
              structure: l[x],
              key: x,
              path,
              prop: l[x].prop,
              isSetting: false,
              isOptional
            });
            if (r) {
              let childStructure = getFromCommonStructure(l[x].def);
              if (!childStructure)
                throw new Error(
                  `BUG. Cannot find structure for ${joinDefined(".", path, l[x].def)} in ${JSON.stringify(structure)}`
                );

              iterate(childStructure, joinDefined(".", path, l[x].prop), isOptional);
            }
          } else if (l[x].val) {
            enter({
              structure: l[x],
              key: x,
              path,
              prop: l[x].prop,
              isSetting: false,
              isOptional
            });
          }
          break;
        }
        default: {
          if (!l[x]) throw new Error(`BUG. A setting is expected at ${x}`);

          enter({
            isSetting: true,
            key: x,
            structure: l[x],
            path,
            prop: x,
            isOptional
          });

          break;
        }
      }
    }
  };

  iterate(structure, "", false);
};

export const iterateStructure = (structure: SettingsStructure, enter: IterateStructureEnter, cl?: GenericMap<any>) =>
  iterateStructureInner(BASE_STRUCTURE, structure, enter, cl);

export type SettingsStructureEnterSetting = SettingsStructureSetting & {
  name: string;
  path: string;
  definition: SettingDefinition;
};

const getPropName = (key: string) => key.replace(/[\>\{\}\+\(\)\[\]]*/g, "");

type GetStructureValueEnterArgs = IterateStructureEnterArgs & {
  setValue: (path: string | undefined, name: string, value: any) => void;
  res: GenericMap<any>;
};
export const getStructureValueBaseInner = (
  baseStructure: GenericMap<any>,
  structure: GenericMap<any>,
  enter: (args: GetStructureValueEnterArgs) => boolean,
  cl?: GenericMap<any>
): GenericMap<any> => {
  const res: GenericMap<any> = {
    _desktop: {
      $count: 0
    },
    _tablet: {
      $count: 0
    },
    _mobile: {
      $count: 0
    }
  };

  const _setValue = (target: GenericMap<any>, path: string, value) => {
    if (isUndefined(get(target, path))) {
      set(target, path, value);
      target.$count++;
    }
  };

  const setValue = (path: string | undefined, name: string, value: any) => {
    if (typeof value === "object") {
      if (value && ("_mobile" in value || "_tablet" in value || "_desktop" in value)) {
        if ("_mobile" in value) {
          _setValue(res._mobile, joinDefined(".", path, name), value._mobile);
        }

        if ("_tablet" in value) {
          _setValue(res._tablet, joinDefined(".", path, name), value._tablet);
        }

        if ("_desktop" in value) {
          _setValue(res._desktop, joinDefined(".", path, name), value._desktop);
        }
      } else if (!isUndefined(value)) {
        _setValue(res._desktop, joinDefined(".", path, name), value);
      }
    } else {
      if (!isUndefined(value)) {
        _setValue(res._desktop, joinDefined(".", path, name), value);
      }
    }
  };

  iterateStructureInner(baseStructure, structure, (args) => enter({ ...args, setValue, res }), cl);

  delete res._desktop.$count;

  if (res._mobile.$count || res._tablet.$count) {
    if (res._mobile.$count) {
      delete res._mobile.$count;
    } else {
      delete res._mobile;
    }

    if (res._tablet.$count) {
      delete res._tablet.$count;
    } else {
      delete res._tablet;
    }

    return res;
  }

  return res._desktop;
};
export const getStructureValueInner = (
  baseStructure: SettingsStructure,
  commonDefinitionsAll: GenericMap<SettingDefinition>,
  structure: GenericMap<any>,
  definitions: GenericMap<SettingDefinition> | null,
  getValue: (setting: SettingsStructureEnterSetting) => any,
  cl?: GenericMap<any>
): GenericMap<any> => {
  const getInner = (structure: GenericMap<any>) =>
    getStructureValueBaseInner(
      baseStructure,
      structure,
      ({ setValue, res, ...args }) => {
        if (args.isSetting) {
          const setting: SettingsStructureSetting =
            typeof args.structure === "string"
              ? {
                  def: args.structure
                }
              : (args.structure as SettingsStructureSetting);

          try {
            const value = getValue({
              ...setting,
              name: args.key,
              path: args.path,
              definition: getDefinitionInner(commonDefinitionsAll, args.key, setting, definitions)
            });
            setValue(args.path, args.key, value);
            return false;
          } catch (e) {
            console.error(args, e);

            throw e;
          }
        }

        const val = args.key === "$" ? args.structure.val : args.structure.$?.val;
        if (val) {
          const parentPath = joinDefined(".", args.path, args.prop);
          if (typeof val !== "object") throw new Error(`BUG. val must be object at ${parentPath}`);

          const isRootResponsive = isResponsiveValue(val);
          traverseObject(val, (obj, key, path) => {
            if (typeof obj[key] !== "object") {
              path = isRootResponsive ? path.split(".").slice(1).join(".") : path;

              setValue(joinDefined(".", parentPath, path), key, obj[key]);
            } else if (isResponsiveValue(obj[key])) {
              if (isRootResponsive)
                throw new Error(`BUG. val at ${parentPath} cannot be responsive AND contain responsive values`);

              setValue(joinDefined(".", parentPath, path), key, obj[key]);
            }
          });

          return true;
        }

        let proceed = !(args.key.startsWith("+") || args.key.startsWith("("));
        return (
          proceed ||
          // if the value is already set, we have to proceed to fulfil it with defaults
          !!get(res, joinDefined(".", "_desktop", args.path, args.prop)) ||
          !!get(res, joinDefined(".", "_tablet", args.path, args.prop)) ||
          !!get(res, joinDefined(".", "_mobile", args.path, args.prop))
        );
      },
      cl
    );

  return getInner(structure);
};
export const getStructureValue = (
  structure: GenericMap<any>,
  definitions: GenericMap<SettingDefinition> | null,
  getValue: (setting: SettingsStructureEnterSetting) => any,
  cl?: GenericMap<any>
): GenericMap<any> =>
  getStructureValueInner(BASE_STRUCTURE, COMMON_DEFINITIONS_ALL, structure, definitions, getValue, cl);

export const getStructureDefaultValueInner = (
  baseStructure: SettingsStructure,
  commonDefinitionsAll: GenericMap<SettingDefinition>,
  cacheKey: string,
  structure: SettingsStructure,
  definitions: GenericMap<SettingDefinition> | null,
  cl?: SettingsStructure
): GenericMap<any> => {
  if (!(cacheKey in defaultValueCache)) {
    defaultValueCache[cacheKey] = getStructureValueInner(
      baseStructure,
      commonDefinitionsAll,
      structure,
      definitions,
      (setting) => (isUndefined(setting.val) ? setting.definition.def : setting.val),
      cl
    );
  }
  return defaultValueCache[cacheKey];
};

const defaultValueCache: GenericMap<GenericMap<any>> = {};
export const getStructureDefaultValue = (
  cacheKey: string,
  structure: SettingsStructure,
  definitions: GenericMap<SettingDefinition> | null,
  cl?: SettingsStructure
): GenericMap<any> =>
  getStructureDefaultValueInner(BASE_STRUCTURE, COMMON_DEFINITIONS_ALL, cacheKey, structure, definitions, cl);

export const getStructureElementValueInner = (
  baseStructure: SettingsStructure,
  commonDefinitionsAll: GenericMap<SettingDefinition>,
  structure: SettingsStructure,
  screen: ScreenType,
  definitions: GenericMap<SettingDefinition> | null,
  cl?: SettingsStructure
): any => {
  const value = getStructureValueInner(
    baseStructure,
    commonDefinitionsAll,
    structure,
    definitions,
    (setting) => (isUndefined(setting.val) ? setting.definition.def : setting.val),
    cl
  );

  return value && typeof value === "object" ? value[`_${screen}`] ?? value : value;
};
export const getStructureElementValue = (
  structure: SettingsStructure,
  screen: ScreenType,
  definitions: GenericMap<SettingDefinition> | null,
  cl?: SettingsStructure
): any => getStructureElementValueInner(BASE_STRUCTURE, COMMON_DEFINITIONS_ALL, structure, screen, definitions, cl);
